import React from 'react';
import { Grid, Container } from '@material-ui/core';
import ActivityCard from './ActivityCard';
import ErrorBoundary from '../ErrorBoundary';
import { CalendarIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { getDealRoomBrandedName } from '../../utils/locale';

const GettingStarted = ({ current_user, company, features = [] }) => {
  const flagEnabled = Array.isArray(features) ? features.includes('triggering_event_getting_started') : false;
  const events = Array.isArray(current_user.triggering_events) ? current_user.triggering_events : [];
  const hasEvent = (type) => events.some((e) => e.event_type === type);
  // Function to check if an activity is completed
  const isActivityCompleted = (activityId) => {
    // We need to check the completion status based on the activity type
    switch(activityId) {
      case 'find_my_gaps': {
        // Prefer explicit boolean if provided by backend (Rails may serialize method names with ?)
        const explicit = current_user['has_completed_triggering_events?'];
        if (explicit !== undefined && explicit !== null) return Boolean(explicit);
        // Alternate camel/snake shapes without the question mark
        if (current_user.has_completed_triggering_events !== undefined) {
          return Boolean(current_user.has_completed_triggering_events);
        }
        // Fallback: infer from triggering_events array
        const evts = Array.isArray(current_user.triggering_events) ? current_user.triggering_events : [];
        return evts.some(e => e && e.completed_at);
      }
      case 'valuation':
        return current_user.valuation_last_accessed_date !== null;
      case 'profile':
        return current_user.profile_last_accessed_date !== null;
      case 'discovery':
        return current_user['has_used_discovery?']
      case 'bcp':
        return current_user.bcp_form_datum && (current_user.bcp_form_datum.status === 'in_progress' || current_user.bcp_form_datum.status === 'completed');
      case 'catastrophe':
        return current_user.catastrophe_form_datum && (current_user.catastrophe_form_datum.status === 'in_progress' || current_user.catastrophe_form_datum.status === 'completed');
      case 'succession_planning':
        return current_user.succession_planning_form_datum && (current_user.succession_planning_form_datum.status === 'in_progress' || current_user.succession_planning_form_datum.status === 'completed');
      case 'education':
        return current_user['has_viewed_education?']
      case 'wealth_gap': {
        if (flagEnabled) return hasEvent('wealth_gap');
        return current_user['has_wealth_gap_data?'] === true || current_user.has_wealth_gap_data === true
      }
      case 'owner_diagnostic':
        return flagEnabled ? hasEvent('owner_diagnostic') : (current_user.diag_completed_at != null)
      case 'profit_gap': {
        if (flagEnabled) return hasEvent('profit_gap');
        // Wealth RJ tenants don't populate recurring_revenue; use gross_production
        const isWealth = (company && company.business_type) === 'wealth_rj'
        if (current_user.profit_gap_recasted_ebitda != null) return true
        if (isWealth) {
          return (current_user.gross_production != null) && (current_user.normalized_operating_expenses != null)
        }
        return (current_user.recurring_revenue != null) && (current_user.normalized_operating_expenses != null)
      }
      case 'value_gap': {
        if (flagEnabled) return hasEvent('value_gap');
        const prereqs = isActivityCompleted('wealth_gap') && isActivityCompleted('profit_gap') && isActivityCompleted('owner_diagnostic');
        return prereqs && (current_user.value_gap_current_value !== null && current_user.value_gap_gap_value !== null);
      }
      case 'consultation':
        return current_user.consultation_booked_at !== null;
      case 'deal_room':
        return current_user.inventory_user && current_user.inventory_user.deals_count > 0;
      default:
        return false;
    }
  };

  // Calculate marketplace-style progress
  const getMarketplaceProgress = () => {
    const suggestedActivities = current_user.suggested_activities || [];
    const completedActivities = suggestedActivities.filter(activityId => isActivityCompleted(activityId));
    const total = suggestedActivities.length || 5;
    const completed = completedActivities.length;
    const percentage = total > 0 ? (completed / total) * 100 : 0;
    
    return {
      total,
      completed,
      percentage: Math.round(percentage)
    };
  };

  const { total, completed, percentage } = getMarketplaceProgress();

  // Get headline and subtext based on motivation with marketplace theming
  const getHeadlineContent = () => {
    const { motivation } = current_user;
    
    switch(motivation) {
      case 'succession':
        return {
          headline: "Welcome to your succession marketplace.",
          subtext: "Discover tools and partners to help you build your succession plan."
        };
      case 'growth':
        return {
          headline: "Explore growth opportunities in the marketplace.",
          subtext: "Find the resources and connections you need to expand your practice."
        };
      case 'no_motivation':
      default:
        return {
          headline: "Welcome to the FindBob marketplace.",
          subtext: "Explore our suite of tools and resources designed for your success."
        };
    }
  };
  
  const { headline, subtext } = getHeadlineContent();
  const suggestedActivities = current_user.suggested_activities || [];
  
  // Map activity IDs to full activity objects with succession theming
  const activityMap = {
    find_my_gaps: {
      id: 'find_my_gaps',
      title: 'Find My Gaps',
      description: 'Quick checkup surfaces value + readiness gaps and builds your plan.',
      icon: 'TargetIcon',
      path: '#open-intake',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    wealth_gap: {
      id: 'wealth_gap',
      title: "What's Your Wealth Gap?",
      description: 'Assess your Wealth Gap and get tailored next steps',
      icon: 'CurrencyDollarIcon',
      path: '/succession/readiness/wealth-gap',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    owner_diagnostic: {
      id: 'owner_diagnostic',
      title: 'Business Attractiveness vs. Buyer Readiness',
      description: 'Take a quick diagnostic to see your quadrant and next steps',
      icon: 'ClipboardDocumentIcon',
      path: '/succession/readiness/owner-diagnostic',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    profit_gap: {
      id: 'profit_gap',
      title: 'What is your Profit Gap?',
      description: 'Compare your business to best-in-class peers',
      icon: 'ChartBarIcon',
      path: '/succession/readiness/profit-gap',
      bgColor: 'bg-succession-info/10',
      iconColor: 'text-succession-info',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    value_gap: {
      id: 'value_gap',
      title: 'Value Gap',
      description: 'See the gap between your Wealth Goal and current value',
      icon: 'CurrencyDollarIcon',
      path: '/succession/readiness/value-gap',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    valuation: {
      id: 'valuation',
      title: 'Run Valuation',
      description: 'Understand your practice\'s value with our DCF and MOR tools',
      icon: 'ChartBarIcon',
      path: '/succession/valuation',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    profile: {
      id: 'profile',
      title: 'Edit Profile',
      description: 'Update your profile to reflect your goals and preferences',
      icon: 'UserIcon',
      path: '/succession/profile',
      bgColor: 'bg-succession-success/10',
      iconColor: 'text-succession-success',
      borderColor: 'border-succession-border hover:border-succession-success'
    },
    discovery: {
      id: 'discovery',
      title: 'Find Partners',
      description: 'Find potential partners that match your criteria',
      icon: 'SearchIcon',
      path: '/succession/search',
      bgColor: 'bg-succession-info/10',
      iconColor: 'text-succession-info',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    bcp: {
      id: 'bcp',
      title: 'Create Continuity Plan',
      description: 'Ensure business continuity with our guided planner',
      icon: 'DocumentTextIcon',
      path: '/succession/bcp',
      bgColor: 'bg-succession-warning/10',
      iconColor: 'text-succession-warning',
      borderColor: 'border-succession-border hover:border-succession-warning'
    },
    catastrophe: {
      id: 'catastrophe',
      title: 'Create Catastrophic Plan',
      description: 'Prepare for unexpected events with our planning tool',
      icon: 'ExclamationCircleIcon',
      path: '/succession/catastrophe',
      bgColor: 'bg-succession-error/10',
      iconColor: 'text-succession-error',
      borderColor: 'border-succession-border hover:border-succession-error'
    },
    succession_planning: {
      id: 'succession_planning',
      title: 'Succession Planning',
      description: 'Create a comprehensive succession plan for your practice',
      icon: 'ClipboardDocumentIcon',
      path: '/succession/succession_planning',
      bgColor: 'bg-succession-primary/10',
      iconColor: 'text-succession-primary',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    education: {
      id: 'education',
      title: 'Educational Library',
      description: 'Learn more about succession planning and practice valuation',
      icon: 'AcademicCapIcon',
      path: '/succession/education',
      bgColor: 'bg-succession-info/10',
      iconColor: 'text-succession-info',
      borderColor: 'border-succession-border hover:border-succession-primary'
    },
    consultation: {
      id: 'consultation',
      title: 'Book a Consultation',
      description: 'Schedule time with a platform concierge who can assist with your transition journey',
      icon: 'CalendarIcon',
      path: company?.calendly_link || 'https://calendly.com/findbob/consultation',
      bgColor: 'bg-gray-100',
      iconColor: 'text-gray-600',
      borderColor: 'border-succession-border hover:border-gray-400'
    },
    deal_room: {
      id: 'deal_room',
      title: `Create a ${getDealRoomBrandedName(company)}`,
      description: `Create a ${getDealRoomBrandedName(company)} to define your sell-side objectives and streamline the process`,
      icon: 'BuildingOfficeIcon',
      path: '/succession/deals',
      bgColor: 'bg-gray-100',
      iconColor: 'text-gray-600',
      borderColor: 'border-succession-border hover:border-gray-400'
    }
  };
  
  // Map activity IDs to full activity objects
  const mappedActivities = suggestedActivities.map(activityId => activityMap[activityId]).filter(Boolean);
  const valueGapLocked = !(isActivityCompleted('wealth_gap') && isActivityCompleted('profit_gap') && isActivityCompleted('owner_diagnostic'));
  
  return (
    <div className="flex h-screen bg-succession-bg-secondary transition-colors duration-300">
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-succession-bg-secondary p-6 transition-colors duration-300">
          <Container maxWidth="xl">
            {/* Marketplace-style header */}
            <div className="text-center mb-8 mt-8">
              <h1 className="text-3xl font-bold text-succession-text-primary mb-4 transition-colors duration-300">
                {headline}
              </h1>
              <p className="text-xl text-succession-text-secondary max-w-3xl mx-auto transition-colors duration-300">
                {subtext}
              </p>
            </div>
            

            
            {/* Activity cards with succession theming */}
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-succession-text-primary mb-4 transition-colors duration-300">
                Recommended Activities
              </h2>
              <p className="text-succession-text-secondary mb-6 transition-colors duration-300">
                Complete these activities to maximize your marketplace experience
              </p>
            </div>
            
            <Grid container spacing={4} justifyContent="center">
              {mappedActivities.map((activity) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={activity.id}>
                  <ErrorBoundary>
                    <ActivityCard 
                      activity={activity} 
                      completed={isActivityCompleted(activity.id)} 
                      locked={activity.id === 'value_gap' && valueGapLocked}
                    />
                  </ErrorBoundary>
                </Grid>
              ))}
            </Grid>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default GettingStarted;
